import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Gift, User, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    birthday: "",
    address: "",
    city: "",
    postal_code: "",
    country: ""
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      // Séparer le nom complet en prénom et nom
      const nameParts = (response.data.name || "").split(' ');
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(' ') || "";
      
      setProfileData({
        first_name: firstName,
        last_name: lastName,
        birthday: response.data.birthday || "",
        address: response.data.address || "",
        city: response.data.city || "",
        postal_code: response.data.postal_code || "",
        country: response.data.country || ""
      });
    } catch (error) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      // Combiner prénom et nom pour l'envoi au backend
      const dataToSend = {
        ...profileData,
        name: `${profileData.first_name} ${profileData.last_name}`.trim()
      };
      delete dataToSend.first_name;
      delete dataToSend.last_name;
      
      await axios.put(`${API}/auth/profile`, dataToSend, { withCredentials: true });
      toast.success("Profil mis à jour !");
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de mise à jour");
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { withCredentials: true });
      
      toast.success("Mot de passe modifié !");
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="bg-card/80 backdrop-blur-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <User className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="font-heading text-xl font-bold" data-testid="profile-title">Mon Compte</h1>
              <p className="text-sm text-muted-foreground">@{user?.username}</p>
            </div>
          </div>
          <Button 
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')} 
            variant="ghost" 
            size="sm"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Mes cadeaux
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="profile" data-testid="profile-tab">
              <User className="w-4 h-4 mr-2" />
              Informations
            </TabsTrigger>
            <TabsTrigger value="password" data-testid="password-tab">
              <Lock className="w-4 h-4 mr-2" />
              Mot de passe
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="font-heading">Informations personnelles</CardTitle>
                <CardDescription>Modifiez vos informations de profil</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">Prénom</Label>
                      <Input
                        data-testid="first-name-input"
                        id="first_name"
                        value={profileData.first_name}
                        onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                        className="mt-1"
                        placeholder="Jean"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Nom</Label>
                      <Input
                        data-testid="last-name-input"
                        id="last_name"
                        value={profileData.last_name}
                        onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                        className="mt-1"
                        placeholder="Dupont"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={user?.email}
                      disabled
                      className="mt-1 bg-muted"
                    />
                    <p className="text-xs text-muted-foreground mt-1">L'email ne peut pas être modifié</p>
                  </div>

                  <div>
                    <Label htmlFor="birthday">Date d'anniversaire</Label>
                    <Input
                      data-testid="birthday-input"
                      id="birthday"
                      type="date"
                      value={profileData.birthday}
                      onChange={(e) => setProfileData({...profileData, birthday: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Adresse postale</Label>
                    <Input
                      data-testid="address-input"
                      id="address"
                      placeholder="123 Rue Exemple"
                      value={profileData.address}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postal_code">Code postal</Label>
                      <Input
                        data-testid="postal-code-input"
                        id="postal_code"
                        placeholder="75001"
                        value={profileData.postal_code}
                        onChange={(e) => setProfileData({...profileData, postal_code: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">Ville</Label>
                      <Input
                        data-testid="city-input"
                        id="city"
                        placeholder="Paris"
                        value={profileData.city}
                        onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="country">Pays</Label>
                    <Input
                      data-testid="country-input"
                      id="country"
                      placeholder="France"
                      value={profileData.country}
                      onChange={(e) => setProfileData({...profileData, country: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <Button 
                    data-testid="save-profile-btn"
                    type="submit" 
                    className="w-full rounded-full py-6 font-bold"
                  >
                    Enregistrer les modifications
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="password">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="font-heading">Changer le mot de passe</CardTitle>
                <CardDescription>Modifiez votre mot de passe de connexion</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <Label htmlFor="current_password">Mot de passe actuel</Label>
                    <Input
                      data-testid="current-password-input"
                      id="current_password"
                      type="password"
                      placeholder="••••••••"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="new_password">Nouveau mot de passe</Label>
                    <Input
                      data-testid="new-password-input"
                      id="new_password"
                      type="password"
                      placeholder="••••••••"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
                    <Input
                      data-testid="confirm-password-input"
                      id="confirm_password"
                      type="password"
                      placeholder="••••••••"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>

                  <Button 
                    data-testid="change-password-btn"
                    type="submit" 
                    className="w-full rounded-full py-6 font-bold"
                  >
                    Modifier le mot de passe
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}